import ComparisonHandle from './ComparisonHandle.js';
import { useEffect, useMemo, useRef, useState } from 'react';

import { authenticatedFetch } from './auth.js';
import { dataUrlToFile, fetchPrivateFile } from './image-transfer.js';
import { BatchStagingArea } from './BatchStagingArea.js';
import ProcessingStatus from './ProcessingStatus.js';
import { createLocalPhotoPreview } from './photo-preview.js';
import PhotoEditArea from './PhotoEditArea.js';
import FloorPlanArea from './FloorPlanArea.js';
import { MAX_TEXT_EDIT_LENGTH, TEXT_EDIT_EXAMPLES } from './shared/text-edit.js';
import ObjectFolders from './ObjectFolders.js';
import {
  PHOTO_ADJUSTMENT_LIMITS,
  resetPhotoAdjustments,
  type PhotoAdjustmentName,
  type PhotoAdjustments,
} from './shared/photo-adjustments.js';

const ROOM_TYPES = ['Wohnzimmer', 'Schlafzimmer', 'Esszimmer', 'Arbeitszimmer', 'Kinderzimmer', 'Küche', 'Badezimmer', 'Aussenbereich'] as const;
const STYLES = ['Modern', 'Skandinavisch', 'Luxuriös'] as const;
const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;
const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

type RoomType = (typeof ROOM_TYPES)[number];
type InteriorStyle = (typeof STYLES)[number];
type EditMode = 'furnish' | 'empty' | 'tidy' | 'tidy-thorough' | 'improve' | 'text';
type SelectionPurpose = 'remove' | 'protect';
type ActiveArea = 'staging' | 'batch' | 'sky' | 'dusk' | 'renovate' | 'floorplan2d' | 'floorplan3d';

function isTidyEditMode(mode: EditMode): boolean {
  return mode === 'tidy' || mode === 'tidy-thorough';
}

function isTextEditMode(mode: EditMode): boolean {
  return mode === 'text';
}

function ObjectFolderDownload({ objectId }: { objectId: string | null }) {
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    if (!objectId) { setCount(null); return; }
    let cancelled = false;
    void authenticatedFetch('/api/objects').then((response) => response.json()).then((payload) => {
      if (!cancelled) setCount(Number(payload.objects?.find((folder: { id: string }) => folder.id === objectId)?.resultCount || 0));
    }).catch(() => { if (!cancelled) setCount(0); });
    return () => { cancelled = true; };
  }, [objectId]);
  if (!objectId) return null;
  return <section className="object-folder-download wrap" aria-label="Ergebnisbilder herunterladen"><p className="field-hint">Alle fertigen Bearbeitungen dieser Mappe, ohne Originalfotos.</p>{count && count > 0 ? <a className="secondary-button" href={`/api/objects/${objectId}/results.zip`}>Alle Ergebnisbilder herunterladen ({count})</a> : <span className="secondary-button is-disabled" aria-disabled="true">Alle Ergebnisbilder herunterladen (0)</span>}</section>;
}

function isTargetedEditMode(mode: EditMode): boolean {
  return isTidyEditMode(mode) || isTextEditMode(mode);
}

function canSelectRegions(mode: EditMode): boolean {
  return mode === 'empty' || isTargetedEditMode(mode);
}

interface ProtectedRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface AppStatus {
  apiKeyConfigured: boolean;
  model: string;
  provider: string;
  uploadLimitBytes: number;
  maxBatchImages?: number;
}

interface StageResult {
  image: string;
  originalImage: string;
  imageUrl?: string;
  originalImageUrl?: string;
  downloadUrl?: string;
  originalDownloadUrl?: string;
  mode: EditMode;
  promptSummary: string;
  original: { width: number; height: number };
  result: { width: number; height: number };
  note: string;
  sourceFileId?: string;
  resultFileId?: string;
}

const PHOTO_SLIDERS: Array<{ key: PhotoAdjustmentName; label: string; suffix: string; hint: string }> = [
  { key: 'brightness', label: 'Helligkeit', suffix: '%', hint: 'dunkler · heller' },
  { key: 'contrast', label: 'Kontrast', suffix: '%', hint: 'weicher · kräftiger' },
  { key: 'temperature', label: 'Farbtemperatur', suffix: '', hint: 'kühler · wärmer' },
  { key: 'saturation', label: 'Sättigung', suffix: '%', hint: 'zurückhaltender · intensiver' },
];

interface ApiErrorPayload {
  error?: { code?: string; message?: string };
}

function formatBytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

function getPortalDashboardUrl(): string {
  const referrer = document.referrer;
  try {
    const referrerUrl = new URL(referrer);
    if (referrerUrl.pathname === '/dashboard') return referrerUrl.href;
  } catch { /* Kein Portal-Referrer vorhanden. */ }

  if ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && window.location.port === '8787') {
    return `${window.location.protocol}//${window.location.hostname}:5173/dashboard`;
  }
  return '/dashboard';
}

function clampUnit(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function pointerPoint(event: React.PointerEvent<HTMLDivElement>) {
  const bounds = event.currentTarget.getBoundingClientRect();
  return {
    x: clampUnit((event.clientX - bounds.left) / bounds.width),
    y: clampUnit((event.clientY - bounds.top) / bounds.height),
  };
}

function regionBetween(start: { x: number; y: number }, end: { x: number; y: number }): ProtectedRegion {
  return {
    x: Math.min(start.x, end.x),
    y: Math.min(start.y, end.y),
    width: Math.abs(end.x - start.x),
    height: Math.abs(end.y - start.y),
  };
}

function Icon({ name }: { name: 'upload' | 'spark' | 'download' | 'arrow' | 'check' }) {
  if (name === 'upload') {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 16V4m0 0 4.5 4.5M12 4 7.5 8.5M5 13v5.5A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V13" /></svg>;
  }
  if (name === 'spark') {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 2 1.5 6.5L20 10l-6.5 1.5L12 18l-1.5-6.5L4 10l6.5-1.5L12 2Zm7 15 .7 2.3L22 20l-2.3.7L19 23l-.7-2.3L16 20l2.3-.7L19 17Z" /></svg>;
  }
  if (name === 'download') {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12m0 0 4.5-4.5M12 15l-4.5-4.5M5 20h14" /></svg>;
  }
  if (name === 'arrow') {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h13m-5-5 5 5-5 5" /></svg>;
  }
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4.5 4.5L19 7" /></svg>;
}

function App() {
  const [status, setStatus] = useState<AppStatus | null>(null);
  const [statusError, setStatusError] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [roomType, setRoomType] = useState<RoomType>('Wohnzimmer');
  const [style, setStyle] = useState<InteriorStyle>('Modern');
  const [editMode, setEditMode] = useState<EditMode>('furnish');
  const [result, setResult] = useState<StageResult | null>(null);
  const [textResultCandidate, setTextResultCandidate] = useState<StageResult | null>(null);
  const [textInstruction, setTextInstruction] = useState('');
  const [textSource, setTextSource] = useState<'original' | 'result'>('original');
  const [comparePosition, setComparePosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [processingPhase, setProcessingPhase] = useState<'uploading' | 'queued' | 'processing' | 'preparing'>('processing');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [previewDimensions, setPreviewDimensions] = useState<{ width: number; height: number } | null>(null);
  const [protectedRegions, setProtectedRegions] = useState<ProtectedRegion[]>([]);
  const [removalRegions, setRemovalRegions] = useState<ProtectedRegion[]>([]);
  const [selectionPurpose, setSelectionPurpose] = useState<SelectionPurpose>('remove');
  const [selectionStart, setSelectionStart] = useState<{ x: number; y: number } | null>(null);
  const [draftRegion, setDraftRegion] = useState<ProtectedRegion | null>(null);
  const [photoAdjustments, setPhotoAdjustments] = useState<PhotoAdjustments>(resetPhotoAdjustments);
  const [isEnhancingPreview, setIsEnhancingPreview] = useState(false);
  const [activeArea, setActiveArea] = useState<ActiveArea>('staging');
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);
  const [sourceVersionId, setSourceVersionId] = useState<string | null>(null);
  const [sourceSelection, setSourceSelection] = useState<'result' | 'original'>('result');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void authenticatedFetch('/api/status')
      .then(async (response) => {
        if (!response.ok) throw new Error('Status konnte nicht geladen werden.');
        return response.json() as Promise<AppStatus>;
      })
      .then(setStatus)
      .catch(() => setStatusError('Der lokale Server ist nicht erreichbar. Starte die Anwendung neu.'));
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    async function readExistingJob() {
      try {
        const response = await authenticatedFetch('/api/jobs');
        if (!response.ok) return;
        const payload = await response.json() as { jobs?: Array<{ id: string; mode: string; status: 'queued' | 'running' | 'succeeded' | 'failed'; errorCode?: string | null }> };
        const stageModes = new Set(['furnish', 'empty', 'tidy', 'tidy-thorough', 'text', 'sky', 'dusk', 'renovate', 'improve']);
        const activeJob = payload.jobs?.find((job) => stageModes.has(job.mode) && (job.status === 'queued' || job.status === 'running'));
        if (!activeJob || cancelled) return;
        setIsGenerating(true);
        setProcessingPhase(activeJob.status === 'queued' ? 'queued' : 'processing');
        setMessage(activeJob.status === 'queued' ? 'Auftrag wartet auf Bearbeitung …' : 'Bild wird bearbeitet …');
        const poll = async () => {
          try {
            const currentResponse = await authenticatedFetch(`/api/jobs/${activeJob.id}`);
            if (!currentResponse.ok) return;
            const current = (await currentResponse.json() as { job?: typeof activeJob }).job;
            if (!current || cancelled) return;
            if (current.status === 'queued' || current.status === 'running') {
              setProcessingPhase(current.status === 'queued' ? 'queued' : 'processing');
              setMessage(current.status === 'queued' ? 'Auftrag wartet auf Bearbeitung …' : 'Bild wird bearbeitet …');
              timer = window.setTimeout(() => void poll(), 1200);
            } else {
              setIsGenerating(false);
              if (current.status === 'failed') setError('Der bestehende Bildauftrag ist fehlgeschlagen. Bitte starte bewusst einen neuen Auftrag.');
              else setMessage('Der bestehende Bildauftrag ist abgeschlossen. Das gespeicherte Ergebnis kann über «Letztes Ergebnis laden» abgerufen werden.');
            }
          } catch { /* Die sichtbare Bearbeitung bleibt erhalten, bis der Server wieder antwortet. */ }
        };
        timer = window.setTimeout(() => void poll(), 1200);
      } catch { /* Nicht angemeldete oder lokale Ansichten bleiben bedienbar. */ }
    }
    void readExistingJob();
    return () => { cancelled = true; if (timer) window.clearTimeout(timer); };
  }, []);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  useEffect(() => {
    if (editMode !== 'improve' || !previewUrl) {
      setIsEnhancingPreview(false);
      return;
    }
    let cancelled = false;
    setIsEnhancingPreview(true);
    void createLocalPhotoPreview(previewUrl, photoAdjustments)
      .then((localPreview) => {
        if (cancelled) return;
        setResult({
          image: localPreview.dataUrl,
          originalImage: previewUrl,
          mode: 'improve',
          promptSummary: 'Foto verbessern · lokale Bildverarbeitung',
          original: { width: localPreview.sourceWidth, height: localPreview.sourceHeight },
          result: { width: localPreview.sourceWidth, height: localPreview.sourceHeight },
          note: 'Das Bild wurde lokal aus dem unveränderten Original berechnet. Gegenstände, Architektur, Bildausschnitt und Auflösung des Exports bleiben erhalten; prüfe die natürliche Wirkung vor der Verwendung.',
        });
        setComparePosition(50);
        setIsEnhancingPreview(false);
      })
      .catch((previewError) => {
        if (cancelled) return;
        setIsEnhancingPreview(false);
        setError(previewError instanceof Error ? previewError.message : 'Die lokale Bildvorschau ist fehlgeschlagen.');
      });
    return () => { cancelled = true; };
  }, [editMode, photoAdjustments, previewUrl]);

  const comparisonRatio = useMemo(() => {
    if (!result) return 16 / 10;
    return result.original.width / result.original.height;
  }, [result]);

  function rejectFile(reason: string) {
    setSelectedFile(null);
    setResult(null);
    setSourceVersionId(null);
    setTextResultCandidate(null);
    setPreviewDimensions(null);
    setProtectedRegions([]);
    setRemovalRegions([]);
    setSelectionPurpose(editMode === 'empty' ? 'protect' : 'remove');
    setSelectionStart(null);
    setDraftRegion(null);
    setPhotoAdjustments(resetPhotoAdjustments());
    setError(reason);
    setMessage('');
  }

  function handleFile(file?: File) {
    if (!file || isGenerating || isEnhancingPreview) return;
    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      setError('Bitte wähle ein JPG-, PNG- oder WebP-Bild. Der Server prüft den tatsächlichen Dateityp zusätzlich.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setError('Die Datei ist zu gross. Bitte verwende ein Bild bis maximal 15 MB.');
      return;
    }

    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setResult(null);
    setSourceVersionId(null);
    setSourceSelection('original');
    setTextResultCandidate(null);
    setPreviewDimensions(null);
    setProtectedRegions([]);
    setRemovalRegions([]);
    setSelectionPurpose(editMode === 'empty' ? 'protect' : 'remove');
    setSelectionStart(null);
    setDraftRegion(null);
    setPhotoAdjustments(resetPhotoAdjustments());
    setTextSource('original');
    setError('');
    setMessage(editMode === 'empty'
      ? 'Foto bereit. Prüfe den Raumtyp und starte danach bewusst das Leeren.'
      : editMode === 'tidy'
        ? 'Foto bereit. Prüfe den Raumtyp und starte danach bewusst das Aufräumen.'
        : editMode === 'tidy-thorough'
          ? 'Foto bereit. Prüfe den Raumtyp und starte danach bewusst das gründliche Aufräumen.'
          : editMode === 'improve'
            ? 'Foto bereit. Die Vorschau wird lokal aus dem Original berechnet; ein API-Aufruf ist dafür nicht nötig.'
        : 'Foto bereit. Wähle Raumtyp und Stil und starte danach bewusst die Möblierung.');
  }

  function handleDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragging(false);
    if (isGenerating || isEnhancingPreview) return;
    handleFile(event.dataTransfer.files[0]);
  }

  function openSourceFilePicker(event?: React.SyntheticEvent) {
    event?.stopPropagation();
    if (!isGenerating && !isEnhancingPreview) fileInputRef.current?.click();
  }

  function startProtectedSelection(event: React.PointerEvent<HTMLDivElement>) {
    if (event.button !== 0 || !canSelectRegions(editMode)) return;
    const point = pointerPoint(event);
    setSelectionStart(point);
    setDraftRegion({ x: point.x, y: point.y, width: 0, height: 0 });
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function moveProtectedSelection(event: React.PointerEvent<HTMLDivElement>) {
    if (!selectionStart || !canSelectRegions(editMode)) return;
    setDraftRegion(regionBetween(selectionStart, pointerPoint(event)));
  }

  function finishProtectedSelection(event: React.PointerEvent<HTMLDivElement>) {
    if (!selectionStart || !canSelectRegions(editMode)) return;
    const region = regionBetween(selectionStart, pointerPoint(event));
    if (region.width >= 0.02 && region.height >= 0.02) {
      if (activeSelectionPurpose === 'protect') {
        setProtectedRegions((current) => [...current, region]);
      } else {
        setRemovalRegions((current) => [...current, region]);
      }
    }
    setSelectionStart(null);
    setDraftRegion(null);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  }

  function removeSelectedRegion(purpose: SelectionPurpose, index: number) {
    if (purpose === 'remove') {
      setRemovalRegions((current) => current.filter((_, regionIndex) => regionIndex !== index));
    } else {
      setProtectedRegions((current) => current.filter((_, regionIndex) => regionIndex !== index));
    }
  }

  function selectEditMode(nextMode: EditMode) {
    if (nextMode === editMode || isGenerating) return;
    if (result) setTextResultCandidate(result);
    setEditMode(nextMode);
    setResult(null);
    setError('');
    setSelectionStart(null);
    setDraftRegion(null);
    setSelectionPurpose(nextMode === 'empty' ? 'protect' : 'remove');
    if (nextMode === 'improve') setPhotoAdjustments(resetPhotoAdjustments());
    if (nextMode === 'text') setTextSource('original');
    setMessage(nextMode === 'empty'
      ? 'Werkzeug «Raum leeren» gewählt. Der Moduswechsel löst keine Bildbearbeitung aus.'
      : nextMode === 'tidy'
        ? 'Werkzeug «Aufräumen» gewählt. Persönliche Gegenstände und Unordnung werden entfernt; Möbel und feste Einbauten bleiben erhalten. Der Moduswechsel löst keine Bildbearbeitung aus.'
        : nextMode === 'tidy-thorough'
          ? 'Werkzeug «Gründlich aufräumen» gewählt. Auch bewusst platzierte persönliche Gegenstände werden entfernt; Möbel und Architektur bleiben erhalten. Der Moduswechsel löst keine Bildbearbeitung aus.'
          : nextMode === 'improve'
            ? 'Werkzeug «Foto verbessern» gewählt. Helligkeit, Kontrast, Farbtemperatur und Sättigung werden lokal aus dem Original berechnet; der Moduswechsel löst keinen API-Aufruf aus.'
          : nextMode === 'text'
            ? 'Werkzeug «Bild per Texteingabe bearbeiten» gewählt. Wähle das Ausgangsbild, formuliere die Änderung und starte sie bewusst.'
        : 'Werkzeug «Virtuell möblieren» gewählt. Wähle einen Stil und starte die Bearbeitung bewusst.');
  }

  async function createStaging() {
    if (!selectedObjectId) {
      setError('Wähle zuerst eine Objektmappe unter «Meine Objekte» aus oder lege eine neue an.');
      return;
    }
    if (!selectedFile) {
      setError('Bitte lade zuerst ein Foto hoch.');
      return;
    }
    if (editMode === 'improve') {
      if (!previewUrl) {
        setError('Die lokale Bildvorschau ist noch nicht bereit.');
        return;
      }
      setIsGenerating(true);
      setProcessingPhase('processing');
      setError('');
      setMessage('Foto wird lokal aus dem unveränderten Original berechnet …');
      try {
        const localPreview = await createLocalPhotoPreview(previewUrl, photoAdjustments);
        setResult({
          image: localPreview.dataUrl,
          originalImage: previewUrl,
          mode: 'improve',
          promptSummary: 'Foto verbessern · lokale Bildverarbeitung',
          original: { width: localPreview.sourceWidth, height: localPreview.sourceHeight },
          result: { width: localPreview.sourceWidth, height: localPreview.sourceHeight },
          note: 'Das Bild wurde lokal aus dem unveränderten Original berechnet. Gegenstände, Architektur, Bildausschnitt und Auflösung des Exports bleiben erhalten; prüfe die natürliche Wirkung vor der Verwendung.',
        });
        setComparePosition(50);
        setMessage('Lokale Vorschau bereit. Für den Export wird dieselbe Berechnung in Originalauflösung verwendet.');
      } catch (localError) {
        setError(localError instanceof Error ? localError.message : 'Die lokale Bildverarbeitung ist fehlgeschlagen.');
        setMessage('');
      } finally {
        setIsGenerating(false);
      }
      return;
    }
    const normalizedTextInstruction = textInstruction.trim();
    if (editMode === 'text') {
      if (!normalizedTextInstruction) {
        setError('Bitte beschreibe zuerst, was am Bild geändert werden soll.');
        return;
      }
      if (normalizedTextInstruction.length > MAX_TEXT_EDIT_LENGTH) {
        setError(`Die Bildanweisung ist zu lang. Bitte beschränke sie auf maximal ${MAX_TEXT_EDIT_LENGTH} Zeichen.`);
        return;
      }
      if (textSource === 'result' && !textResultCandidate) {
        setError('Wähle ein vorhandenes Ergebnis erst ausdrücklich als Ausgangsbild aus.');
        return;
      }
    }
    if (!status?.apiKeyConfigured) {
      setError('API-Schlüssel fehlt. Es wird kein simuliertes Ergebnis erzeugt.');
      return;
    }

    setIsGenerating(true);
    setProcessingPhase('uploading');
    setError('');
    setMessage('Bild wird hochgeladen …');
    const formData = new FormData();
    let inputFile = selectedFile;
    if (editMode === 'text' && textSource === 'result' && textResultCandidate) {
      try {
        inputFile = textResultCandidate.resultFileId
          ? await fetchPrivateFile(textResultCandidate.resultFileId, 'vorheriges-ergebnis.png')
          : dataUrlToFile(textResultCandidate.image, 'vorheriges-ergebnis.png');
      } catch {
        setIsGenerating(false);
        setError('Das ausgewählte Ergebnis konnte nicht als Ausgangsbild gelesen werden.');
        setMessage('');
        return;
      }
    }
    if (!sourceVersionId) formData.append('image', inputFile);
    formData.append('sourceOrientationRevision', String(sourceOrientationRevision));
    if (selectedObjectId) formData.append('objectId', selectedObjectId);
    if (sourceVersionId) { formData.append('sourceVersionId', sourceVersionId); formData.append('sourceSelection', sourceSelection); }
    formData.append('mode', editMode);
    formData.append('roomType', roomType);
    if (editMode === 'furnish') formData.append('style', style);
    if (editMode === 'text') {
      formData.append('instruction', normalizedTextInstruction);
      formData.append('textSource', textSource);
    }
    if (editMode === 'empty' || editMode === 'tidy-thorough' || editMode === 'text') formData.append('protectedRegions', JSON.stringify(protectedRegions));
    if (isTidyEditMode(editMode) || editMode === 'text') formData.append('editRegions', JSON.stringify(removalRegions));

    try {
      setProcessingPhase('processing');
      setMessage('Bild wird bearbeitet …');
      const response = await authenticatedFetch('/api/stage', { method: 'POST', body: formData });
      const payload = await response.json() as StageResult & ApiErrorPayload;
      if (!response.ok) {
        throw new Error(payload.error?.message || 'Die Bildbearbeitung ist fehlgeschlagen.');
      }
      const completedResult = {
        ...payload,
        image: payload.imageUrl || payload.image || '',
        originalImage: payload.originalImageUrl || payload.originalImage || previewUrl,
        mode: payload.mode || editMode,
      };
      setResult(completedResult);
      if (editMode === 'text') setTextResultCandidate(completedResult);
      setComparePosition(50);
      setMessage(editMode === 'empty'
        ? 'Raum geleert. Prüfe rekonstruierte und zuvor verdeckte Flächen sorgfältig.'
        : editMode === 'tidy'
          ? 'Raum aufgeräumt. Prüfe, ob persönliche Gegenstände entfernt und Möbel, Kaminsims sowie Aussenbereich erhalten blieben.'
          : editMode === 'tidy-thorough'
            ? 'Raum gründlich aufgeräumt. Prüfe, ob nur Gegenstände entfernt und Möbel, Leuchten, Kaminsims sowie Aussenbereich erhalten blieben.'
          : editMode === 'text'
            ? 'Änderung erstellt. Prüfe, ob nur der gewünschte Bereich verändert wurde und alle übrigen Details erhalten blieben.'
          : 'Ergebnis bereit. Prüfe die Geometrie sorgfältig, bevor du es verwendest.');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Die Bildbearbeitung ist fehlgeschlagen.');
      setMessage('');
    } finally {
      setIsGenerating(false);
    }
  }

  async function downloadImprovedResult() {
    if (!selectedFile) {
      setError('Bitte lade zuerst ein Foto hoch.');
      return;
    }
    setIsGenerating(true);
    setProcessingPhase('processing');
    setError('');
    setMessage('PNG wird lokal in Originalauflösung exportiert …');
    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      if (selectedObjectId) formData.append('objectId', selectedObjectId);
      for (const slider of PHOTO_SLIDERS) formData.append(slider.key, String(photoAdjustments[slider.key]));
      const response = await authenticatedFetch('/api/improve', { method: 'POST', body: formData });
      if (!response.ok) {
        const payload = await response.json().catch(() => null) as ApiErrorPayload | null;
        throw new Error(payload?.error?.message || 'Der lokale PNG-Export ist fehlgeschlagen.');
      }
      const blob = await response.blob();
      if (!blob.size) throw new Error('Der lokale PNG-Export ist leer.');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'property-partners-foto-verbessert.png';
      link.click();
      URL.revokeObjectURL(url);
      setMessage('Bearbeitetes PNG vollständig und ohne Text- oder Logo-Einblendung heruntergeladen.');
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : 'Der lokale PNG-Export ist fehlgeschlagen.');
      setMessage('');
    } finally {
      setIsGenerating(false);
    }
  }

  async function downloadLatestResult() {
    setError('');
    setMessage('Unbeschriftete Rohdatei wird geprüft …');
    try {
      const response = await authenticatedFetch('/api/stage/latest');
      if (!response.ok) {
        const payload = await response.json().catch(() => null) as ApiErrorPayload | null;
        throw new Error(payload?.error?.message || 'Die Rohdatei des letzten Ergebnisses ist nicht vorhanden.');
      }
      const blob = await response.blob();
      if (!blob.size) throw new Error('Die Rohdatei des letzten Ergebnisses ist leer oder nicht mehr vorhanden.');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const headerFilename = response.headers.get('content-disposition')?.match(/filename="([^"]+)"/i)?.[1];
      link.download = headerFilename || 'property-partners-bearbeitung.png';
      link.click();
      URL.revokeObjectURL(url);
      setMessage('Unbeschriftetes API-Ergebnis vollständig heruntergeladen. Der Hinweis bleibt in der Benutzeroberfläche sichtbar.');
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : 'Die Rohdatei des letzten Ergebnisses ist nicht vorhanden.');
      setMessage('');
    }
  }

  async function deleteStoredResult() {
    if (!result?.resultFileId) {
      setError('Für dieses Ergebnis ist keine serverseitige Rohdatei bekannt.');
      return;
    }
    setError('');
    try {
      for (const fileId of [result.sourceFileId, result.resultFileId].filter((value): value is string => Boolean(value))) {
        const response = await authenticatedFetch(`/api/files/${fileId}`, { method: 'DELETE' });
        if (!response.ok) {
          const payload = await response.json().catch(() => null) as ApiErrorPayload | null;
          throw new Error(payload?.error?.message || 'Die serverseitige Rohdatei konnte nicht gelöscht werden.');
        }
      }
      setMessage('Die zugehörigen serverseitigen Rohdateien wurden gelöscht. Der lokale Vergleich bleibt bis zum Verlassen der Sitzung sichtbar.');
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Die serverseitigen Rohdateien konnten nicht gelöscht werden.');
    }
  }

  async function useEmptyResultForStaging() {
    if (!result || result.mode !== 'empty') return;
    try {
      const file = result.resultFileId
        ? await fetchPrivateFile(result.resultFileId, 'raum-leer.png')
        : dataUrlToFile(result.image, 'raum-leer.png');
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setPreviewDimensions(null);
      setProtectedRegions([]);
      setRemovalRegions([]);
      setSelectionPurpose('remove');
      setSelectionStart(null);
      setDraftRegion(null);
      setResult(null);
      setEditMode('furnish');
      setError('');
      setMessage('Das geleerte Bild ist als neues Ausgangsbild übernommen. Klicke bewusst auf «Möblierung erstellen», um es zu möblieren.');
    } catch (handoffError) {
      setError(handoffError instanceof Error ? handoffError.message : 'Das geleerte Bild konnte nicht übernommen werden.');
      setMessage('');
    }
  }

  async function continuePhotoResult(fileId: string, target: 'staging' | 'text') {
    try {
      const file = await fetchPrivateFile(fileId, 'bearbeitetes-ergebnis.png');
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setSelectedFile(file); setPreviewUrl(URL.createObjectURL(file)); setPreviewDimensions(null); setProtectedRegions([]); setRemovalRegions([]); setResult(null); setTextResultCandidate(null); setTextSource('original'); setSourceVersionId(null); setEditMode(target === 'text' ? 'text' : 'furnish'); setActiveArea('staging'); setMessage('Das Ergebnis wurde als kompatibles Ausgangsbild übernommen. Starte die nächste Bearbeitung erst bewusst.'); setError('');
    } catch (handoffError) { setError(handoffError instanceof Error ? handoffError.message : 'Das Ergebnis konnte nicht übernommen werden.'); }
  }

  const [floorplanSource, setFloorplanSource] = useState<{ file: File; fileId: string } | null>(null);

  const [versionRefresh, setVersionRefresh] = useState(0);
  const [floorplanSaving, setFloorplanSaving] = useState(false);
  const [floorplanResultVersion, setFloorplanResultVersion] = useState<{ id: string; requestId: string } | null>(null);
  const [sourceOrientationRevision, setSourceOrientationRevision] = useState(0);

  async function useObjectVersion(versionId: string, source: 'result' | 'original' | 'view') {
    try {
      if (source === 'view') { setFloorplanResultVersion({ id: versionId, requestId: crypto.randomUUID() }); setActiveArea('floorplan2d'); return; }
      const response = await authenticatedFetch(`/api/objects/${selectedObjectId}`); const payload = await response.json() as { versions?: Array<{ id: string; mode?: string; settings?: { resultOrientation?: { revision: number } }; sourceFileId: string; originalFileId: string; resultFileId: string | null }> ; error?: { message?: string } };
      if (!response.ok) throw new Error(payload.error?.message || 'Version konnte nicht geladen werden.');
      const version = payload.versions?.find((entry) => entry.id === versionId); const fileId = source === 'original' ? version?.originalFileId : version?.resultFileId;
      if (!version || !fileId) throw new Error('Für diese Version ist keine passende Ausgangsdatei vorhanden.');
      setSourceOrientationRevision(source === 'result' ? version.settings?.resultOrientation?.revision || 0 : 0);
      const file = await fetchPrivateFile(fileId, source === 'original' ? 'original.png' : 'vorheriges-ergebnis.png');
      if (version.mode?.startsWith('floorplan-') && source === 'original') { setFloorplanSource({ file, fileId }); setActiveArea('floorplan2d'); return; }
      if (previewUrl) URL.revokeObjectURL(previewUrl); setSelectedFile(file); setPreviewUrl(URL.createObjectURL(file)); setPreviewDimensions(null); setProtectedRegions([]); setRemovalRegions([]); setSourceVersionId(versionId); setSourceSelection(source); setResult(null); setTextResultCandidate(null); setEditMode('furnish'); setActiveArea('staging'); setMessage(source === 'original' ? 'Originalversion ausgewählt. «Neue Variante vom Original» startet erst durch ausdrücklichen Klick einen neuen Auftrag.' : 'Ergebnisversion ausgewählt. Gespeicherte Ausrichtung übernommen. Alte Schutz- und Bearbeitungsbereiche wurden zurückgesetzt. «Ergebnis weiterbearbeiten» startet erst durch ausdrücklichen Klick einen neuen Auftrag.'); setError('');
    } catch (versionError) { setError(versionError instanceof Error ? versionError.message : 'Version konnte nicht geladen werden.'); }
  }

  const canGenerate = Boolean(selectedFile && !isGenerating && !isEnhancingPreview && (editMode === 'improve' || status?.apiKeyConfigured));
  const activeSelectionPurpose: SelectionPurpose = editMode === 'empty'
    ? 'protect'
    : isTargetedEditMode(editMode)
      ? selectionPurpose
      : 'remove';
  const regionsSelectable = canSelectRegions(editMode);
  const selectableTextResult = textResultCandidate;
  const displayedSourceImageUrl = editMode === 'text' && textSource === 'result' && selectableTextResult
    ? selectableTextResult.image
    : previewUrl;

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href="#top" aria-label="Property Partners Virtual Staging Startseite">
          <img className="brand-logo" src="/assets/Propp_LOG_Quadrat.png" alt="Property Partners" />
          <span className="brand-context"><strong>Virtual Staging</strong></span>
        </a>
        <div className="topbar-actions"><a className="dashboard-link" href={getPortalDashboardUrl()}>← Dashboard</a><div className="topbar-meta"><span className="status-dot" /> Lokale Vorschau</div></div>
      </header>

      <main id="top">
        <fieldset disabled={floorplanSaving} style={{display: 'contents'}}><ObjectFolders refreshKey={versionRefresh} selectedObjectId={selectedObjectId} onSelect={(id) => { setSelectedObjectId(id); setSourceVersionId(null); }} onUseVersion={(id, source) => void useObjectVersion(id, source)} /></fieldset>
        <ObjectFolderDownload objectId={selectedObjectId} />
        <section className="hero wrap">
          <div className="hero-copy">
            <p className="eyebrow">Räume sichtbar machen</p>
            <h1>Leere Räume.<br /><em>Mehr Zuhause.</em></h1>
            <p className="hero-lead">Virtuelles Staging für Immobilienfotos – sorgfältig, transparent und direkt in deinem Browser.</p>
          </div>
          <div className="hero-note" aria-label="Arbeitsablauf">
            <div className="hero-note-number">01</div>
          <div><strong>Ein Projekt, mehrere Werkzeuge</strong><span>Du behältst die Kontrolle über jeden Schritt.</span></div>
            <Icon name="arrow" />
          </div>
        </section>

        <nav className="area-nav wrap" aria-label="Bearbeitungsbereiche">
          {([['staging', 'Foto bearbeiten'], ['batch', 'Mehrere Bilder gleichzeitig stagen'], ['sky', 'Blauer Himmel'], ['dusk', 'Abendstimmung'], ['renovate', 'Renovieren'], ['floorplan2d', '2D-Grundriss'], ['floorplan3d', '3D-Visualisierung']] as Array<[ActiveArea, string]>).map(([area, label]) => <button key={area} type="button" className={activeArea === area ? 'is-selected' : ''} onClick={() => setActiveArea(area)}>{label}</button>)}
        </nav>

        <div hidden={activeArea !== 'staging'}>
        <section className="workspace wrap" aria-label="Virtual Staging Arbeitsbereich">
          <div className="setup-column">
            <div className="section-intro">
              <span className="step-index">01</span>
              <div><p className="eyebrow">Dein Ausgangsbild</p><h2>Foto hochladen</h2></div>
            </div>

            <div
              className={`dropzone ${isDragging ? 'dropzone--active' : ''} ${selectedFile ? 'dropzone--ready' : ''}`}
              onClick={openSourceFilePicker}
              onDragOver={(event) => { event.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => { if ((event.key === 'Enter' || event.key === ' ') && !isGenerating && !isEnhancingPreview) openSourceFilePicker(event); }}
              aria-disabled={isGenerating || isEnhancingPreview}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(event) => { handleFile(event.target.files?.[0]); event.currentTarget.value = ''; }}
                hidden
              />
              {selectedFile && previewUrl ? (
                <div
                  className={`upload-preview selection-stage ${regionsSelectable ? 'selection-stage--active' : ''}`}
                  style={{ aspectRatio: previewDimensions ? `${previewDimensions.width} / ${previewDimensions.height}` : '16 / 10' }}
                  onClick={(event) => event.stopPropagation()}
                  onPointerDown={regionsSelectable ? startProtectedSelection : undefined}
                  onPointerMove={regionsSelectable ? moveProtectedSelection : undefined}
                  onPointerUp={regionsSelectable ? finishProtectedSelection : undefined}
                  onPointerCancel={regionsSelectable ? finishProtectedSelection : undefined}
                  aria-label={isTargetedEditMode(editMode) ? 'Bildfläche: Ziehen zum Markieren der Bearbeitungsflächen' : editMode === 'empty' ? 'Bildfläche: Ziehen zum Markieren geschützter Bereiche' : undefined}
                >
                  <img src={displayedSourceImageUrl} alt={editMode === 'text' && textSource === 'result' ? 'Vorschau des ausdrücklich gewählten vorhandenen Ergebnisses' : 'Vorschau des hochgeladenen Raums'} draggable="false" onLoad={(event) => setPreviewDimensions({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })} />
                  {regionsSelectable && <>
                    <span className="selection-stage-hint">{activeSelectionPurpose === 'protect' ? 'Ziehen zum Markieren des Schutzbereichs' : 'Ziehen zum Markieren der Bearbeitungsfläche'}</span>
                    {(editMode === 'empty' || editMode === 'tidy-thorough' || isTextEditMode(editMode)) && protectedRegions.map((region, index) => (
                      <div
                        className="protected-region protected-region--keep"
                        key={`keep-${region.x}-${region.y}-${index}`}
                        style={{ left: `${region.x * 100}%`, top: `${region.y * 100}%`, width: `${region.width * 100}%`, height: `${region.height * 100}%` }}
                      >
                        <span>Behalten {index + 1}</span>
                        <button type="button" aria-label={`Schutzbereich ${index + 1} entfernen`} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); removeSelectedRegion('protect', index); }}>×</button>
                      </div>
                    ))}
                    {isTargetedEditMode(editMode) && removalRegions.map((region, index) => (
                      <div
                        className="protected-region protected-region--removal"
                        key={`remove-${region.x}-${region.y}-${index}`}
                        style={{ left: `${region.x * 100}%`, top: `${region.y * 100}%`, width: `${region.width * 100}%`, height: `${region.height * 100}%` }}
                      >
                        <span>Bearbeiten {index + 1}</span>
                        <button type="button" aria-label={`Bearbeitungsbereich ${index + 1} entfernen`} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); removeSelectedRegion('remove', index); }}>×</button>
                      </div>
                    ))}
                    {draftRegion && <div className={`protected-region protected-region--draft ${activeSelectionPurpose === 'protect' ? 'protected-region--keep' : 'protected-region--removal'}`} style={{ left: `${draftRegion.x * 100}%`, top: `${draftRegion.y * 100}%`, width: `${draftRegion.width * 100}%`, height: `${draftRegion.height * 100}%` }} />}
                  </>}
                  <div className="upload-preview-overlay"><Icon name="check" /><span>{editMode === 'text' && textSource === 'result' && selectableTextResult ? 'Ergebnis als Ausgangsbild' : 'Foto ausgewählt'}</span></div>
                </div>
              ) : (
                <>
                  <span className="upload-icon"><Icon name="upload" /></span>
                  <strong>Foto hier ablegen</strong>
                  <span>oder vom Computer auswählen</span>
                  <small>JPG, PNG oder WebP · maximal 15 MB</small>
                </>
              )}
            </div>

            {selectedFile && <div className="file-line"><span>{selectedFile.name}</span><small>{formatBytes(selectedFile.size)}</small><button type="button" disabled={isGenerating || isEnhancingPreview} onClick={openSourceFilePicker}>Weiteres Foto bearbeiten</button></div>}

            {(editMode === 'empty' || editMode === 'tidy-thorough' || isTextEditMode(editMode)) && <div className="protected-regions-panel">
              <div className="label-row"><strong>Bereiche behalten</strong><span>{protectedRegions.length === 0 ? 'Optional' : `${protectedRegions.length} markiert`}</span></div>
              <p>{selectedFile ? 'Ziehe Rechtecke über Kamin, Kaminsims, Terrasse oder andere Bereiche, die unverändert bleiben sollen.' : 'Lade zuerst ein Bild hoch, um Bereiche zu markieren.'}</p>
              <small>Markierte Bereiche haben Vorrang vor der Texteingabe und werden aus dem Ausgangsbild übernommen – auch Gegenstände darin bleiben erhalten. Sichtbare Übergänge bitte prüfen.</small>
            </div>}

            {isTargetedEditMode(editMode) && <div className="protected-regions-panel removal-regions-panel">
              {(editMode === 'tidy-thorough' || isTextEditMode(editMode)) && <div className="selection-purpose-toggle" role="group" aria-label="Markierungsart">
                <button type="button" className={activeSelectionPurpose === 'remove' ? 'is-selected' : ''} onClick={() => setSelectionPurpose('remove')}>Entfernungsfläche</button>
                <button type="button" className={activeSelectionPurpose === 'protect' ? 'is-selected' : ''} onClick={() => setSelectionPurpose('protect')}>Schutzbereich</button>
              </div>}
              <div className="label-row"><strong>{activeSelectionPurpose === 'protect' ? 'Bereiche behalten' : isTextEditMode(editMode) ? 'Bearbeitungsflächen markieren' : 'Gegenstände zum Entfernen markieren'}</strong><span>{(activeSelectionPurpose === 'protect' ? protectedRegions : removalRegions).length === 0 ? 'Optional' : `${(activeSelectionPurpose === 'protect' ? protectedRegions : removalRegions).length} markiert`}</span></div>
              <p>{selectedFile
                ? activeSelectionPurpose === 'protect'
                  ? 'Ziehe Rechtecke über Leuchten, Heizkörper, TV-Gestell oder andere wichtige Elemente, die vollständig erhalten bleiben sollen.'
                  : isTextEditMode(editMode)
                    ? 'Ziehe Rechtecke über die Flächen, in denen die Texteingabe wirken darf. Nur diese Flächen werden zur Bearbeitung freigegeben.'
                    : 'Ziehe Rechtecke über persönliche Gegenstände oder Unordnung, die entfernt werden soll. Nur diese Flächen werden zur Bearbeitung freigegeben.'
                : 'Lade zuerst ein Bild hoch, um Bereiche zu markieren.'}</p>
              <small>{activeSelectionPurpose === 'protect'
                ? 'Auch Gegenstände innerhalb eines Schutzbereichs bleiben erhalten. Sichtbare Übergänge bitte prüfen.'
                : isTextEditMode(editMode)
                  ? 'Ohne Bearbeitungsflächen bleibt die Textbearbeitung möglich; die Erhaltung aller übrigen Details ist dann nicht garantiert. Ausserhalb markierter Flächen werden Originalpixel übernommen; Schutzbereiche haben Vorrang.'
                  : 'Ohne Markierungen bleibt der automatische Modus verfügbar. Die gezielte Auswahl bietet mehr Kontrolle. Ausserhalb der markierten Flächen werden die Originalpixel übernommen; Übergänge bitte prüfen.'}</small>
            </div>}

            <div className="form-block">
              <div className="label-row"><label>Werkzeug</label><span>Eine Bearbeitung pro Auftrag</span></div>
              <div className="mode-grid" role="radiogroup" aria-label="Bildbearbeitung">
                <button className={`mode-choice ${editMode === 'furnish' ? 'is-selected' : ''}`} type="button" role="radio" aria-checked={editMode === 'furnish'} disabled={isGenerating} onClick={() => selectEditMode('furnish')}>
                  <Icon name="spark" /><span><strong>Virtuell möblieren</strong><small>Möbel und Dekoration ergänzen</small></span>
                </button>
                <button className={`mode-choice ${editMode === 'empty' ? 'is-selected' : ''}`} type="button" role="radio" aria-checked={editMode === 'empty'} disabled={isGenerating} onClick={() => selectEditMode('empty')}>
                  <Icon name="check" /><span><strong>Raum leeren</strong><small>Bewegliche Einrichtung entfernen</small></span>
                </button>
                <button className={`mode-choice ${editMode === 'tidy' ? 'is-selected' : ''}`} type="button" role="radio" aria-checked={editMode === 'tidy'} disabled={isGenerating} onClick={() => selectEditMode('tidy')}>
                  <Icon name="check" /><span><strong>Aufräumen</strong><small>Persönliche Gegenstände und Unordnung entfernen</small></span>
                </button>
                <button className={`mode-choice ${editMode === 'tidy-thorough' ? 'is-selected' : ''}`} type="button" role="radio" aria-checked={editMode === 'tidy-thorough'} disabled={isGenerating} onClick={() => selectEditMode('tidy-thorough')}>
                  <Icon name="check" /><span><strong>Gründlich aufräumen</strong><small>Auch persönliche Dekoration und lose Textilien entfernen</small></span>
                </button>
                <button className={`mode-choice ${editMode === 'improve' ? 'is-selected' : ''}`} type="button" role="radio" aria-checked={editMode === 'improve'} disabled={isGenerating} onClick={() => selectEditMode('improve')}>
                  <Icon name="spark" /><span><strong>Foto verbessern</strong><small>Helligkeit und Farbe lokal korrigieren</small></span>
                </button>
                <button className={`mode-choice ${editMode === 'text' ? 'is-selected' : ''}`} type="button" role="radio" aria-checked={editMode === 'text'} disabled={isGenerating} onClick={() => selectEditMode('text')}>
                  <Icon name="spark" /><span><strong>Bild per Texteingabe bearbeiten</strong><small>Eine konkrete Bildänderung beschreiben</small></span>
                </button>
              </div>
              {editMode === 'empty' && <p className="field-hint">Verdeckte Flächen werden passend geschätzt rekonstruiert und müssen danach geprüft werden.</p>}
              {editMode === 'tidy' && <p className="field-hint">Entfernt persönliche Gegenstände und Unordnung. Möbel und feste Einbauten bleiben erhalten.</p>}
              {editMode === 'tidy-thorough' && <p className="field-hint">Entfernt auch bewusst platzierte persönliche Gegenstände, lose Decken und zusätzliche Zierkissen. Möbel, Leuchten und Architektur bleiben erhalten.</p>}
              {editMode === 'text' && <p className="field-hint">Nur die ausdrücklich beschriebene Änderung wird angefordert. Ohne Markierungen ist die Erhaltung aller übrigen Details nicht garantiert.</p>}
            </div>

            <div className="form-block">
              <label htmlFor="room-type">Raumtyp</label>
              <select id="room-type" value={roomType} onChange={(event) => setRoomType(event.target.value as RoomType)}>
                {ROOM_TYPES.map((room) => <option key={room}>{room}</option>)}
              </select>
            </div>

            {editMode === 'text' && <div className="form-block text-edit-block">
              <div className="label-row"><label htmlFor="text-edit-instruction">Was möchtest du ändern?</label><span>{textInstruction.length} / {MAX_TEXT_EDIT_LENGTH}</span></div>
              <textarea
                id="text-edit-instruction"
                value={textInstruction}
                maxLength={MAX_TEXT_EDIT_LENGTH}
                rows={5}
                placeholder="Beschreibe eine konkrete Änderung am Bild …"
                onChange={(event) => setTextInstruction(event.target.value)}
                aria-describedby="text-edit-help"
              />
              <p id="text-edit-help" className="field-hint">Beschreibe nur die gewünschte Bildänderung. Der Text wird als Bildbearbeitungsauftrag behandelt, niemals als Code oder Systemanweisung.</p>
              <div className="text-examples" aria-label="Beispielanweisungen">
                <span>Beispiele</span>
                {TEXT_EDIT_EXAMPLES.map((example) => <button key={example} type="button" onClick={() => setTextInstruction(example)}>{example}</button>)}
              </div>
              <div className="text-source-block">
                <div className="label-row"><strong>Ausgangsbild</strong><span>bewusste Auswahl</span></div>
                <div className="text-source-grid" role="radiogroup" aria-label="Ausgangsbild für die Texteingabe">
                  <button type="button" className={`text-source-choice ${textSource === 'original' ? 'is-selected' : ''}`} role="radio" aria-checked={textSource === 'original'} disabled={!selectedFile} onClick={() => setTextSource('original')}>
                    <strong>Hochgeladenes Original</strong><small>{selectedFile ? selectedFile.name : 'Noch kein Foto ausgewählt'}</small>
                  </button>
                  <button type="button" className={`text-source-choice ${textSource === 'result' ? 'is-selected' : ''}`} role="radio" aria-checked={textSource === 'result'} disabled={!selectableTextResult} onClick={() => setTextSource('result')}>
                    <strong>Vorhandenes Ergebnis</strong><small>{selectableTextResult ? `${selectableTextResult.promptSummary} · ausdrücklich gewählt` : 'Noch kein Ergebnis vorhanden'}</small>
                  </button>
                </div>
                <p className="field-hint">Aktiv: <strong>{textSource === 'result' && selectableTextResult ? 'Vorhandenes Ergebnis' : 'Hochgeladenes Original'}</strong>. Ein vorhandenes Ergebnis wird nur nach dieser Auswahl als Ausgangsbild verwendet.</p>
              </div>
            </div>}

            {editMode === 'furnish' && <div className="form-block">
              <div className="label-row"><label>Einrichtungsstil</label><span>Ein Stil pro Entwurf</span></div>
              <div className="style-grid" role="radiogroup" aria-label="Einrichtungsstil">
                {STYLES.map((option) => (
                  <button key={option} type="button" className={`style-choice style-choice--${option.toLowerCase()} ${style === option ? 'is-selected' : ''}`} onClick={() => setStyle(option)} role="radio" aria-checked={style === option}>
                    <span className="style-swatch"><i /><i /><i /></span>
                    <span>{option}</span>
                    {style === option && <Icon name="check" />}
                  </button>
                ))}
              </div>
              <p className="field-hint">Stilvorschauen · Beispiele, keine generierten Ergebnisbilder.</p>
            </div>}

            {editMode === 'improve' && <div className="form-block enhancement-block">
              <div className="label-row"><label>Foto verbessern</label><span>lokale Verarbeitung</span></div>
              <p className="field-hint enhancement-intro">Passe das unveränderte Original moderat an. Vorschau und Export verwenden dieselbe Pixelberechnung; es wird kein externer API-Aufruf ausgelöst.</p>
              <div className="enhancement-controls">
                {PHOTO_SLIDERS.map((slider) => {
                  const value = photoAdjustments[slider.key];
                  const limits = PHOTO_ADJUSTMENT_LIMITS[slider.key];
                  const displayedValue = value > 0 ? `+${value}` : String(value);
                  return <div className="enhancement-control" key={slider.key}>
                    <div className="label-row"><label htmlFor={`photo-${slider.key}`}>{slider.label}</label><output htmlFor={`photo-${slider.key}`}>{displayedValue}{slider.suffix}</output></div>
                    <input id={`photo-${slider.key}`} type="range" min={limits.min} max={limits.max} step={limits.step} value={value} onChange={(event) => setPhotoAdjustments((current) => ({ ...current, [slider.key]: Number(event.target.value) }))} />
                    <div className="slider-hint"><span>{slider.hint.split(' · ')[0]}</span><span>{slider.hint.split(' · ')[1]}</span></div>
                  </div>;
                })}
              </div>
              <button className="secondary-button enhancement-reset" type="button" disabled={!selectedFile || Object.values(photoAdjustments).every((value) => value === 0)} onClick={() => setPhotoAdjustments(resetPhotoAdjustments())}>Zurücksetzen</button>
            </div>}

            {status && !status.apiKeyConfigured && editMode !== 'improve' && (
              <div className="notice notice--warning" role="status">
                <span className="notice-icon">!</span>
                <div><strong>API-Schlüssel fehlt</strong><p>Lege <code>OPENAI_API_KEY</code> in der lokalen <code>.env</code>-Datei an. Es wird keine fingierte Möblierung angezeigt.</p></div>
              </div>
            )}
            {statusError && <div className="notice notice--error" role="alert"><span className="notice-icon">!</span><p>{statusError}</p></div>}
            {editMode === 'improve' && <div className="notice notice--info" role="status"><span className="notice-icon"><Icon name="check" /></span><p>Foto verbessern arbeitet lokal. Gegenstände und Architektur werden nicht generiert oder rekonstruiert.</p></div>}
            {error && <div className="notice notice--error" role="alert"><span className="notice-icon">!</span><p>{error}</p></div>}
            {message && !error && <div className="notice notice--info" role="status"><span className="notice-icon"><Icon name="check" /></span><p>{message}</p></div>}

            <button className="primary-button" type="button" disabled={!canGenerate} onClick={() => void createStaging()}>
              {isGenerating || isEnhancingPreview ? <><span className="spinner" /> Bild wird bearbeitet …</> : <><Icon name={editMode === 'furnish' || editMode === 'improve' || editMode === 'text' ? 'spark' : 'check'} /> {editMode === 'empty' ? 'Raum leeren' : editMode === 'tidy' ? 'Aufräumen' : editMode === 'tidy-thorough' ? 'Gründlich aufräumen' : editMode === 'improve' ? 'Foto verbessern' : editMode === 'text' ? 'Änderung erstellen' : 'Möblierung erstellen'} <Icon name="arrow" /></>}
            </button>
            <p className="privacy-note">Das Original bleibt erhalten. {editMode === 'improve' ? 'Foto verbessern bleibt lokal; es gibt keine externe Bildgenerierung.' : 'Bilddaten werden nur für die angeforderte Bearbeitung an den konfigurierten Provider übergeben.'} Es gibt keine Veröffentlichung oder Portal-Anbindung.</p>
          </div>

          <div className="result-column">
            <div className="section-intro section-intro--result">
              <span className="step-index">02</span>
              <div><p className="eyebrow">Prüfen und entscheiden</p><h2>Vorher / Nachher</h2></div>
              {result && <span className="result-pill"><span className="status-dot" /> Bereit zur Prüfung</span>}
            </div>

            <ProcessingStatus
              visible={isGenerating || isEnhancingPreview}
              message={message || (isEnhancingPreview ? 'Vorschau wird vorbereitet …' : 'Bild wird bearbeitet …')}
              phase={isEnhancingPreview ? 'preparing' : processingPhase}
            />

            {result ? (
              <div className="result-card">
                <div className="compare-shell" style={{ aspectRatio: comparisonRatio }}>
                  <img className="compare-image compare-image--before" src={result.originalImage} alt="Ausgangsfoto des Raums" />
                  <div className="compare-after" style={{ clipPath: `inset(0 ${100 - comparePosition}% 0 0)` }}>
                    <img className="compare-image" src={result.image} alt={result.mode === 'empty' ? 'Komplett geleerter Raum' : result.mode === 'tidy' ? 'Aufgeräumter Raum' : result.mode === 'tidy-thorough' ? 'Gründlich aufgeräumter Raum' : result.mode === 'improve' ? 'Lokal verbessertes Foto' : result.mode === 'text' ? 'Per Texteingabe bearbeitetes Bild' : 'Virtuell möblierter Raum'} />
                  </div>
                  <ComparisonHandle value={comparePosition} onChange={setComparePosition}/>
                  <span className="compare-label compare-label--before">Vorher</span>
                  <span className="compare-label compare-label--after">Nachher</span>
                </div>
                <div className="compare-control"><span>Vorher</span><input type="range" min="0" max="100" value={comparePosition} onChange={(event) => setComparePosition(Number(event.target.value))} aria-label="Vorher-Nachher-Regler" /><span>Nachher</span></div>
                <div className="result-meta"><span>{result.promptSummary}</span><span>Original {result.original.width} × {result.original.height}px · Ergebnis {result.result.width} × {result.result.height}px</span></div>
                <div className="geometry-note"><Icon name="check" /><span>Beide Bilder werden vollständig eingepasst. Unterschiedliche Proportionen werden nicht still beschnitten oder gestreckt.</span></div>
                <button className="secondary-button" type="button" onClick={() => void (result.mode === 'improve' ? downloadImprovedResult() : downloadLatestResult())}><Icon name="download" /> Unbeschriftetes PNG herunterladen</button>
                {result.mode === 'empty' && <button className="secondary-button" type="button" onClick={() => void useEmptyResultForStaging()}><Icon name="spark" /> Mit diesem Bild neu möblieren</button>}
                {result.resultFileId && <button className="text-danger-button" type="button" onClick={() => void deleteStoredResult()}>Serverseitige Rohdateien dieses Ergebnisses löschen</button>}
                <p className="result-disclaimer">{result.note}</p>
              </div>
            ) : (
              <div className="empty-result">
                <div className="empty-result-art"><span className="empty-window" /><span className="empty-floor" /><span className="empty-line empty-line--one" /><span className="empty-line empty-line--two" /></div>
                <p className="eyebrow">Deine Vorschau</p>
                <h3>Noch kein Ergebnis</h3>
                <p>Lade ein Foto hoch und wähle ein Werkzeug. Nach einer echten Bildbearbeitung erscheint hier der Vergleich.</p>
                <div className="empty-result-foot"><span>Keine generierten Bilder voreingestellt</span><button className="latest-download" type="button" onClick={() => void downloadLatestResult()}><Icon name="download" /> Letztes Ergebnis laden</button></div>
              </div>
            )}
          </div>
        </section>
        </div>

        <BatchStagingArea active={activeArea === 'batch'} maxImages={status?.maxBatchImages} objectId={selectedObjectId} />

        <PhotoEditArea mode="sky" active={activeArea === 'sky'} objectId={selectedObjectId} onContinue={continuePhotoResult} />
        <PhotoEditArea mode="dusk" active={activeArea === 'dusk'} objectId={selectedObjectId} onContinue={continuePhotoResult} />
        <PhotoEditArea mode="renovate" active={activeArea === 'renovate'} objectId={selectedObjectId} onContinue={continuePhotoResult} />
        <FloorPlanArea
          onVersionsChanged={() => setVersionRefresh(current => current + 1)}
          onOrientationSaving={setFloorplanSaving}
          restoredVersion={floorplanResultVersion}
          restoredSource={floorplanSource}
          view={activeArea === 'floorplan3d' ? '3d' : '2d'}
          active={activeArea === 'floorplan2d' || activeArea === 'floorplan3d'}
          apiReady={Boolean(status?.apiKeyConfigured)}
          objectId={selectedObjectId}
          onOpen2d={() => setActiveArea('floorplan2d')}
          onOpen3d={() => setActiveArea('floorplan3d')}
        />

        <section className="how-it-works wrap">
          <div className="how-copy"><p className="eyebrow">03 · Transparenz</p><h2>Virtuell bedeutet sichtbar gekennzeichnet.</h2><p>Das Ergebnis zeigt eine mögliche Einrichtung, nicht den tatsächlichen Zustand der Immobilie. Bewahre das Original auf und prüfe Raumgrenzen, Türen, Fenster, Boden und Perspektive vor jeder Verwendung.</p></div>
          <div className="how-steps"><div><span>01</span><strong>Hochladen</strong><small>JPG, PNG oder WebP</small></div><div><span>02</span><strong>Stil wählen</strong><small>Modern, skandinavisch oder luxuriös</small></div><div><span>03</span><strong>Prüfen</strong><small>Vorher/Nachher vergleichen</small></div></div>
        </section>
      </main>

      <footer className="footer wrap"><span>Property Partners · Virtual Staging</span><span>Lokale Anwendung · Keine Veröffentlichung</span></footer>
    </div>
  );
}

export default App;
